import {
  CATALOG_HAS_CATALOG_IRI,
  CATALOG_HAS_SCHEMA_IRI,
  CATALOG_HAS_TABLE_IRI,
  CATALOG_LINKED_DATA_SOURCE_IRI,
  CATALOG_LOCAL_IRI,
  CATALOG_PRIMARY_KEY_IRI,
  CATALOG_PRIMARY_REFERENCE_IRI,
  CATALOG_PROVIDED_BY_IRI,
  CATALOG_PROVIDERS_IRI,
  DATA_SOURCE_PREFIX,
  LOCAL_NAMED_GRAPH_IRI,
  RDFS_LABEL_IRI,
} from 'src/templates/queries/iris';
import { BindingResult } from 'src/templates/queries/types';

export type CatalogTableColumnsBindingResult = {
  column: BindingResult;
  columnName: BindingResult;
  dataSource: BindingResult;
  table: BindingResult;
  tableName: BindingResult;
  catalogName?: BindingResult;
  schemaName?: BindingResult;
};

/**
 * Queries for primary key table columns and associated data source, catalog, and schema
 * from the Catalog database.
 *
 * **Call Frequency**
 *
 * Called at the start of the publishing workflow
 *
 * **Context**
 * - Named graphs: default graph (query uses FROM NAMED)
 * - Reasoning: always off
 *
 * **Example**
 * ```sparql
# Table Columns
SELECT DISTINCT ?column ?columnName ?dataSource ?catalogName ?schemaName ?table ?tableName
FROM NAMED <tag:stardog:api:context:local>
WHERE {
  VALUES ?dataSource {  
    <data-source://pilothouse_test_drugs> <data-source://postgres> <data-source://dvdrental>
  }
  GRAPH <tag:stardog:api:catalog:providers> {
      ?provider <tag:stardog:api:catalog:provider:linkedDataSource> ?dataSource ;
  }
  GRAPH <tag:stardog:api:catalog:local> {
      ?dbms <tag:stardog:api:catalog:provider:providedBy> ?provider
  }
  GRAPH ?dbms {
      ?dbms (<tag:stardog:api:catalog:hasCatalog>/<tag:stardog:api:catalog:hasSchema>/<tag:stardog:api:catalog:hasTable>
          | <tag:stardog:api:catalog:hasCatalog>/<tag:stardog:api:catalog:hasTable>
          | <tag:stardog:api:catalog:hasSchema>/<tag:stardog:api:catalog:hasTable>) ?table . 
      ?table <tag:stardog:api:catalog:primaryKey> ?primaryKey .
      ?table <http://www.w3.org/2000/01/rdf-schema#label> ?tableName .
      ?primaryKey <tag:stardog:api:catalog:primaryReference> ?column .
      ?column <http://www.w3.org/2000/01/rdf-schema#label> ?columnName .
      OPTIONAL {
        ?dbms <tag:stardog:api:catalog:hasCatalog> ?catalog .
        ?catalog <tag:stardog:api:catalog:hasSchema> ?schema .
        ?schema <http://www.w3.org/2000/01/rdf-schema#label> ?schemaName .
        ?catalog <http://www.w3.org/2000/01/rdf-schema#label> ?catalogName .
        ?schema <tag:stardog:api:catalog:hasTable> ?table .
      }
      OPTIONAL {
        ?dbms <tag:stardog:api:catalog:hasCatalog> ?catalog .
        ?catalog <tag:stardog:api:catalog:hasTable> ?table .
        ?catalog <http://www.w3.org/2000/01/rdf-schema#label> ?catalogName .
      }
      OPTIONAL {
        ?dbms <tag:stardog:api:catalog:hasSchema> ?schema .
        ?schema <tag:stardog:api:catalog:hasTable> ?table .
        ?schema <http://www.w3.org/2000/01/rdf-schema#label> ?schemaName .
      }
  }
}
 * ```
 */
export const buildCatalogPrimaryKeyTableColumnsQuery = (
  dataSourceIds: string[]
) => {
  return `# Table Columns
SELECT DISTINCT ?column ?columnName ?dataSource ?catalogName ?schemaName ?table ?tableName
FROM NAMED <${LOCAL_NAMED_GRAPH_IRI}>
WHERE {
  VALUES ?dataSource {  
    ${dataSourceIds.map((iri) => `<${DATA_SOURCE_PREFIX}${iri}>`).join(' ')}
  }
  GRAPH <${CATALOG_PROVIDERS_IRI}> {
      ?provider <${CATALOG_LINKED_DATA_SOURCE_IRI}> ?dataSource ;
  }
  GRAPH <${CATALOG_LOCAL_IRI}> {
      ?dbms <${CATALOG_PROVIDED_BY_IRI}> ?provider
  }
  GRAPH ?dbms {
      ?dbms (<${CATALOG_HAS_CATALOG_IRI}>/<${CATALOG_HAS_SCHEMA_IRI}>/<${CATALOG_HAS_TABLE_IRI}>
          | <${CATALOG_HAS_CATALOG_IRI}>/<${CATALOG_HAS_TABLE_IRI}>
          | <${CATALOG_HAS_SCHEMA_IRI}>/<${CATALOG_HAS_TABLE_IRI}>) ?table . 
      ?table <${CATALOG_PRIMARY_KEY_IRI}> ?primaryKey .
      ?table <${RDFS_LABEL_IRI}> ?tableName .
      ?primaryKey <${CATALOG_PRIMARY_REFERENCE_IRI}> ?column .
      ?column <${RDFS_LABEL_IRI}> ?columnName .
      OPTIONAL {
        ?dbms <${CATALOG_HAS_CATALOG_IRI}> ?catalog .
        ?catalog <${CATALOG_HAS_SCHEMA_IRI}> ?schema .
        ?schema <${RDFS_LABEL_IRI}> ?schemaName .
        ?catalog <${RDFS_LABEL_IRI}> ?catalogName .
        ?schema <${CATALOG_HAS_TABLE_IRI}> ?table .
      }
      OPTIONAL {
        ?dbms <${CATALOG_HAS_CATALOG_IRI}> ?catalog .
        ?catalog <${CATALOG_HAS_TABLE_IRI}> ?table .
        ?catalog <${RDFS_LABEL_IRI}> ?catalogName .
      }
      OPTIONAL {
        ?dbms <${CATALOG_HAS_SCHEMA_IRI}> ?schema .
        ?schema <${CATALOG_HAS_TABLE_IRI}> ?table .
        ?schema <${RDFS_LABEL_IRI}> ?schemaName .
      }
  }
}`;
};
